import { Card, CardImage, H5, H6 } from "@sellar-io/components";
import { SupplierListEducateResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../components";

import { pluralize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";

export const EducateConnectSuppliers = ({
  supplierQuery,
}: {
  supplierQuery: UseInfiniteQueryResult<
    InfiniteData<SupplierListEducateResponseType>,
    Error
  >;
}) => {
  const suppliers = useMemo(
    () => supplierQuery.data?.pages.flatMap((page) => page) ?? [],
    [supplierQuery.data],
  );

  const isLoading = supplierQuery.isLoading;

  if (isLoading) return <SpinnerLegacy />;

  return (
    <div
      className={twMerge(
        "flex w-full gap-4 pb-4",
        suppliers.length > 3 && "overflow-x-scroll",
        suppliers.length < 3 && "justify-center",
      )}
    >
      {suppliers.map((supplier) => (
        <EducateConnectSupplierCard key={supplier.id} supplier={supplier} />
      ))}
    </div>
  );
};

const EducateConnectSupplierCard = ({
  supplier,
}: {
  supplier: SupplierListEducateResponseType[number];
}) => {
  const getDeliveryType = (
    connection: SupplierListEducateResponseType[number]["SupplierRetailerConnection"],
  ) => {
    switch (connection?.fulfillmentType) {
      case "direct":
        return "Delivers direct to you";
      case "courier":
        return "Courier delivery";
      case "haulier":
        return "Haulier delivery";
      default:
        return "Collection";
    }
  };

  const getTerms = (
    connection: SupplierListEducateResponseType[number]["SupplierRetailerConnection"],
  ) => {
    if (connection?.paymentTerms === "proforma" || !connection?.paymentTerms)
      return "Pro-forma";
    if (connection.paymentDueCalculateFrom === "endOfFollowingMonth")
      return `EOFM ${connection.paymentDue ? `[+ ${connection.paymentDue} ${pluralize("day", connection.paymentDue)}] credit` : ""}`;
    if (connection.paymentDueCalculateFrom === "endOfMonth")
      return `EOM ${connection.paymentDue ? `[+ ${connection.paymentDue} ${pluralize("day", connection.paymentDue)}] credit` : ""}`;
    if (connection.paymentDue)
      return `${connection.paymentDue} ${pluralize("day", connection.paymentDue)} credit`;
    if (connection.paymentTerms === "credit") return "Credit";
  };

  return (
    <Card
      key={supplier.id}
      className={twMerge(
        "bg-white",
        "relative w-[calc((100%/3)-(32px/3))] min-w-[290px] flex-none snap-center",
      )}
    >
      <CardImage imageUrl={supplier.imageUrl ?? ""} fallback="supplier-hero" />
      <div className={"flex flex-col gap-4 p-4"}>
        <div className="flex gap-3 rounded-lg bg-zinc-100 p-3">
          <ProfilePicture
            size={"large"}
            logoUrl={supplier.Business.logoUrl}
            name={supplier.Business.name}
            className="rounded-full border-2 border-white bg-white"
            fallback="supplier-badge"
          />
          <div className={"flex flex-col"}>
            <H5 className="overflow-hidden text-ellipsis whitespace-nowrap">
              {supplier.Business.name}
            </H5>
            <H6
              className={
                "overflow-hidden text-ellipsis whitespace-nowrap text-gray-500"
              }
            >
              {[
                supplier.Business.DeliveryAddress.city,
                supplier.Business.DeliveryAddress.region,
              ]
                .filter(Boolean)
                .join(", ")}
            </H6>
          </div>
        </div>
        <div className={"flex h-[80px] w-full flex-col gap-1"}>
          <span className="flex items-center gap-2">
            <Icon size={0.8} icon="CHECK_CIRCLE" className="text-emerald-600" />
            {getDeliveryType(supplier.SupplierRetailerConnection)}
          </span>
          {supplier.hasDiscounts && (
            <span className="flex items-center gap-2">
              <Icon
                size={0.8}
                icon="CHECK_CIRCLE"
                className="text-emerald-600"
              />
              Discounts applied
            </span>
          )}
          <span className="flex items-center gap-2">
            <Icon size={0.8} icon="CHECK_CIRCLE" className="text-emerald-600" />
            {getTerms(supplier.SupplierRetailerConnection)}
          </span>
        </div>
      </div>
    </Card>
  );
};
